import { ShiftOld } from '@libs/models/shifts/shift';
import { request } from '../../request';
import { PaginatedResponse } from '@libs/models/common';
import { filterToQueryParams } from '../../utils';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { Filter, FiltersCollection } from '@libs/store/filters';

const FallbackTimezone = TIMEZONE_FALLBACK;

export async function fetchShiftsList(
  page = 1,
  pageSize = 10,
  filters: FiltersCollection = {},
  sorting?: Filter,
): Promise<{ total: number; items: ShiftOld[] }> {
  let filtersToResolve = { ...filters };
  if (sorting) {
    filtersToResolve = {
      ...filters,
      orderBy: sorting,
    };
  }
  const applyFilters = filterToQueryParams(filtersToResolve);
  const response = await request.get<PaginatedResponse<ShiftsListItem[]>>('/v2/shifts/list-view', {
    params: {
      pageNumber: page,
      pageSize: pageSize,
      search: '',
      ...applyFilters,
      ...sorting,
    },
  });

  return {
    items: response.data.items.map(
      (item) =>
        ({
          ...item,
          id: item.id,
          shiftStart: new Date(item.startDate.dateTime),
          shiftEnd: new Date(item.endDate.dateTime),
          shiftLength: new Date(item.endDate.dateTime).getTime() - new Date(item.startDate.dateTime).getTime(),
          // recurrence: item.shiftRotationId ? 'rotation' : item.shiftSeriesId ? 'series' : 'single',
          recurrence: item.recurrenceType
            ? item.recurrenceType.toLowerCase()
            : item.shiftRotationId
              ? 'rotation'
              : item.shiftSeriesId
                ? 'series'
                : 'single',
          recurrenceId: item.shiftRecurrenceId,
          qualifications: item.staffNurseQualificationFilters,
          status: item.status as ShiftOld['status'],
          notes: item.note ? [item.note] : [],
          specialty: item.speciality ? { ...item.speciality, name: item.speciality.title } : item.speciality,
          departmentNode: item.departmentNode,
          timeZone: item.startDate.timeZone || FallbackTimezone,
          assignee: item.staff
            ? {
                ...item.staff,
                avatar: item.staff.imageUrl,
                name: `${item.staff.firstName || ''} ${item.staff.lastName || ''}`,
                assignedByClient: false,
                orientedList: (item.staff.orientedRecords ?? []).map((o) => ({
                  locationId: o.locationId,
                  value: o.oriented === 'Yes',
                })),
              }
            : item.assignRequest?.staff && item.assignRequest?.status === 'Pending'
              ? {
                  ...item.assignRequest.staff,
                  avatar: item.assignRequest.staff.imageUrl,
                  name: `${item.assignRequest.staff.firstName || ''} ${item.assignRequest.staff.lastName || ''}`,
                  assignedByClient: false,
                  orientedList: (item.assignRequest.staff.orientedRecords ?? []).map((o) => ({
                    locationId: o.locationId,
                    value: o.oriented === 'Yes',
                  })),
                }
              : null,

          isShared: item.isShared,
          isUrgent: !!item.isUrgent,
          tags: item.tags || [],
        }) as unknown as ShiftOld,
    ),
    total: response.data.totalCount,
  };
}

type ShiftsListItem = {
  id: string; // 'shift-00000000000000000000000000';
  externalId?: string;
  startDate: {
    dateTime: string; // '2025-04-13T18:53:14';
    timeZone: string;
  };
  endDate: {
    dateTime: string; // '2025-04-13T18:53:14';
    timeZone: string;
  };
  agenciesForShare: {
    id: string; //'staffingpartner-00000000000000000000000000';
    logoURL: string;
    name: string;
    tenantId: string;
  }[];
  assignRequest: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string;
      imageUrl: string;
      firstName: string;
      lastName: string;
      isInternal: true;
      agencyId: string; //'agency-00000000000000000000000000';
      agencyName: string;
      orientedRecords: [
        {
          locationId: string; //'location-00000000000000000000000000';
          oriented: 'Yes' | 'No';
        },
      ];
    };
    declineReason: string;
  };
  staffNurseQualificationFilters: [
    {
      id: string;
      name: string;
      abbreviation: string;
      category: string;
    },
  ];
  speciality: {
    id: string; //'speciality-00000000000000000000000000';
    title: string;
    category: string;
  };
  status: 'Opened';
  note: string;
  departmentNode: {
    id: string; //'departmentnode-00000000000000000000000000';
    name: string;
  };
  subDepartmentNode: {
    id: string; //'departmentnode-00000000000000000000000000';
    name: string;
  };
  staff: {
    id: string; //'staffmember-00000000000000000000000000';
    firstName: string;
    lastName: string;
    imageUrl: string;
    isInternal: boolean;
    agencyId: string;
    agencyName: string;
    orientedRecords: {
      locationId: string; // 'location-01k6rvfdgyqz3772fdfgz3ht5f';
      oriented: string; // 'Yes';
    }[];
  };
  shiftSeriesId?: string | null;
  shiftRotationId?: string | null;
  recurrenceType: 'Series' | 'Rotation';
  shiftRecurrenceId: string;
  locationId: string; //'location-00000000000000000000000000';
  isShared: boolean;
  isArchive: boolean;
  isUrgent: boolean;
  tags: string[];
};
